import { useState, useEffect, useCallback, useRef, type ReactNode } from "react";
import { motion } from "framer-motion";
import { useIsMobile } from "@/hooks/use-mobile";

interface WorkflowScenarioProps {
  steps: string[];
  stepDurations: number[];
  pauseDuration?: number;
  renderAnimation: (currentStep: number, cycleKey: number) => ReactNode;
}

export function WorkflowScenario({
  steps,
  stepDurations,
  pauseDuration = 3000,
  renderAnimation,
}: WorkflowScenarioProps) {
  const isMobile = useIsMobile();
  const [currentStep, setCurrentStep] = useState(-1);
  const [cycleKey, setCycleKey] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const totalSteps = steps.length;
  const hasStarted = useRef(false);

  // IntersectionObserver to detect when component is in viewport
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.25 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const resetAndPlay = useCallback(() => {
    setCurrentStep(-1);
    setCycleKey((k) => k + 1);
    setTimeout(() => setCurrentStep(0), 200);
  }, []);

  // Start animation only when visible
  useEffect(() => {
    if (!isVisible) return;
    if (hasStarted.current) return;
    hasStarted.current = true;
    setCurrentStep(-1);
    setCycleKey(0);
    const timeout = setTimeout(() => setCurrentStep(0), 400);
    return () => clearTimeout(timeout);
  }, [isVisible]);

  // Reset when steps change (tab switch)
  useEffect(() => {
    hasStarted.current = false;
    setCurrentStep(-1);
    setCycleKey(0);
    if (isVisible) {
      hasStarted.current = true;
      const timeout = setTimeout(() => setCurrentStep(0), 400);
      return () => clearTimeout(timeout);
    }
  }, [steps]);

  useEffect(() => {
    if (currentStep < 0) return;
    if (currentStep >= totalSteps) {
      const timeout = setTimeout(resetAndPlay, pauseDuration);
      return () => clearTimeout(timeout);
    }
    const dur = stepDurations[currentStep] ?? 2500;
    const timeout = setTimeout(() => setCurrentStep((s) => s + 1), dur);
    return () => clearTimeout(timeout);
  }, [currentStep, cycleKey, totalSteps, stepDurations, pauseDuration, resetAndPlay]);

  const step = Math.max(0, currentStep);

  return (
    <div ref={containerRef} className={`flex ${isMobile ? "flex-col" : "flex-row"} gap-6 lg:gap-8`}>
      {/* Step list */}
      <div className={`flex flex-col gap-2 ${isMobile ? "mb-4" : "w-56 shrink-0 pt-2"}`}>
        {steps.map((label, i) => {
          const isActive = i <= step;
          const isCurrent = i === step;
          return (
            <div key={i} className="flex items-center gap-3">
              <motion.div
                className="flex items-center justify-center rounded-full text-xs font-heading font-semibold shrink-0"
                style={{ width: 26, height: 26 }}
                animate={{
                  backgroundColor: isCurrent
                    ? "hsl(var(--coral))"
                    : isActive
                    ? "hsl(var(--petrol))"
                    : "hsl(0 0% 85%)",
                  color: isActive ? "#fff" : "hsl(0 0% 50%)",
                  scale: isCurrent ? 1.1 : 1,
                }}
                transition={{ duration: 0.4 }}
              >
                {i + 1}
              </motion.div>
              <span
                className="text-xs lg:text-sm font-body transition-colors duration-300"
                style={{
                  color: isCurrent
                    ? "hsl(var(--coral))"
                    : isActive
                    ? "hsl(var(--foreground))"
                    : "hsl(0 0% 55%)",
                  fontWeight: isCurrent ? 600 : 400,
                }}
              >
                {label}
              </span>
            </div>
          );
        })}
      </div>

      {/* Animation area */}
      <div className="relative flex-1 min-w-0">
        {renderAnimation(step, cycleKey)}
      </div>
    </div>
  );
}
